/**
 * Unified Learner Linkage Domain
 * ──────────────────────────────────────────────────────────────
 *
 * Single source of truth for validating and inspecting **role ↔ learner**
 * linkage decisions across the entire platform.
 *
 * Replaces (as canonical implementation; legacy wrappers remain for
 * backwards-compat):
 *   • `classrooms.ts::validateAddLearnerToClassroom`     (teacher flow)
 *   • `studentUtils.ts::getLearnerClassroomFlags`        (teacher chips)
 *   • Implicit one-off validation in `useLinkChild.ts`   (parent flow)
 *
 * Why this exists
 * ───────────────
 * Before consolidation, three independent code paths each enforced their
 * own subset of warning rules — teacher-flow had `grade_mismatch` but no
 * `multi_parent`, parent-flow had nothing structured at all, and the
 * chip-helper in `studentUtils.ts` re-implemented enrollment scanning.
 * The result was: silent gaps (parents could link a child already shared
 * with two other parents with no confirmation), inconsistent copy, and
 * duplicated scan logic.
 *
 * This module unifies all of that behind two pure functions:
 *
 *   1. `validateLink(state, context)`   → ordered list of typed warnings
 *   2. `getLearnerLinkageFlags(state, learnerId, opts?)` → boolean flags +
 *      enrichment for UI chips/badges
 *
 * Both are pure (no I/O, no mutation) and exhaustively typed so adding a
 * new warning kind triggers TypeScript errors in every consumer.
 *
 * Design contract
 * ───────────────
 * • A "warning" never blocks a flow on its own — UI may gate the confirm
 *   button visually but the underlying mutation always proceeds. Use
 *   severity `'blocking'` only for situations the UI MUST reject (e.g.
 *   `already_linked_self` — re-linking is a no-op).
 * • Warnings are returned in **stable order** (grade → enrollment →
 *   teacher → parent) so the UI can render them deterministically.
 * • Messages are in Dutch (consumer audience) and self-contained — they
 *   include learner display names and concrete numbers so a UI can show
 *   them without further interpolation.
 */

import {
  AccountsState,
  Classroom,
  LearnerAccount,
  isLearnerAccount,
} from '../types'

// ── Public types ─────────────────────────────────────────────────────

/**
 * The context in which a linkage decision is being validated.
 * Discriminated by `kind` so each branch can carry the IDs it needs.
 */
export type LinkContext =
  | {
      kind: 'teacher-classroom'
      teacherId: string
      classroomId: string
      learnerId: string
    }
  | {
      kind: 'teacher-direct'
      teacherId: string
      learnerId: string
    }
  | {
      kind: 'parent-learner'
      parentId: string
      learnerId: string
    }

/**
 * The severity of a warning.
 *
 * • `info`     — purely informational, no special UI required
 * • `warning`  — should be surfaced visibly; UI may gate the confirm CTA
 *                (e.g. amber button) but action remains possible
 * • `blocking` — the UI MUST refuse to proceed; the underlying mutation
 *                would be a no-op or destructive in an unwanted way
 */
export type LinkWarningSeverity = 'info' | 'warning' | 'blocking'

/**
 * The full discriminated union of every possible linkage warning.
 *
 * Adding a new variant here will force TypeScript to flag every consumer
 * that switches on `type` — that is intentional and the whole point of
 * centralizing this contract.
 */
export type LinkWarning =
  /** Learner has a grade set but it differs from the classroom's group level. */
  | {
      type: 'grade_mismatch'
      severity: 'warning'
      message: string
      learnerGrade: number
      classroomGroupLevel: number
    }
  /** Learner has no grade set yet; will be inherited from the classroom. */
  | {
      type: 'grade_unknown'
      severity: 'info'
      message: string
      classroomGroupLevel: number
    }
  /** Learner is already enrolled in another classroom (different teacher). */
  | {
      type: 'multi_classroom'
      severity: 'warning'
      message: string
      classroomName: string
      classroomId: string
      teacherName: string
      teacherId: string
    }
  /** Learner is linked to another teacher (not via a classroom we already warned about). */
  | {
      type: 'multi_teacher'
      severity: 'warning'
      message: string
      teacherName: string
      teacherId: string
    }
  /** Learner already has one or more other parents linked. */
  | {
      type: 'multi_parent'
      severity: 'warning'
      message: string
      otherParents: Array<{ id: string; displayName: string }>
    }
  /** Caller is already linked to this learner. The mutation would be a no-op. */
  | {
      type: 'already_linked_self'
      severity: 'blocking'
      message: string
    }

/**
 * Boolean linkage flags + enrichment for rendering persistent chips/badges
 * on learner rows (teacher portal) or linked-child grids (parent portal).
 *
 * This is the chip-level summary, not the validation result. Use
 * `validateLink()` when the user is performing an action and needs
 * messages; use `getLearnerLinkageFlags()` for ambient display.
 */
export interface LearnerLinkageFlags {
  /** Learner has a `grade` set that differs from the supplied classroom context. */
  gradeMismatch: boolean
  /** Learner has no `grade` set at all. */
  gradeUnknown: boolean
  /** Learner is enrolled in 2+ classrooms total (across all teachers). */
  multiClassroom: boolean
  /** Learner is linked to 2+ teachers (directly or via classrooms). */
  multiTeacher: boolean
  /** Learner is linked to 2+ parents. */
  multiParent: boolean
  /** Other enrollments excluding the supplied classroom context (if any). */
  otherEnrollments: Array<{
    classroomId: string
    classroomName: string
    teacherName: string
    teacherId: string
    groupLevel: 6 | 7 | 8
  }>
  /** Other parents excluding the supplied parent context (if any). */
  otherParents: Array<{ id: string; displayName: string }>
}

/**
 * Options for {@link getLearnerLinkageFlags}. Both are optional so the
 * function can be called from contexts where neither classroom nor parent
 * scope is known (e.g. admin views).
 */
export interface LinkageFlagsOptions {
  /**
   * If provided, `gradeMismatch` and `otherEnrollments` are computed
   * relative to this classroom. If `null` / omitted, every enrollment is
   * "other".
   */
  classroom?: Classroom | null
  /**
   * If provided, the listed parent is excluded from `otherParents` and
   * `multiParent` reflects "are there other parents besides this one?".
   */
  parentId?: string | null
}

// ── Internal helpers ─────────────────────────────────────────────────

interface EnrollmentScan {
  enrollments: Array<{
    classroomId: string
    classroomName: string
    teacherName: string
    teacherId: string
    groupLevel: 6 | 7 | 8
  }>
  teacherIds: Set<string>
}

/**
 * Single-pass scan of all classrooms to collect every enrollment + every
 * teacher (owner + co-teachers) that touches this learner. Pure.
 */
function scanLearnerEnrollments(
  state: AccountsState,
  learnerId: string,
): EnrollmentScan {
  const enrollments: EnrollmentScan['enrollments'] = []
  const teacherIds = new Set<string>()

  for (const cls of Object.values(state.classrooms)) {
    if (!cls.learnerIds.includes(learnerId)) continue
    teacherIds.add(cls.teacherId)
    if (cls.coTeacherIds) {
      for (const id of cls.coTeacherIds) teacherIds.add(id)
    }
    const teacher = state.accountsById[cls.teacherId]
    enrollments.push({
      classroomId: cls.id,
      classroomName: cls.name,
      teacherName: teacher?.displayName || 'Onbekend',
      teacherId: cls.teacherId,
      groupLevel: cls.groupLevel,
    })
  }

  // Direct teacher↔learner links that aren't expressed via a classroom.
  for (const [tid, lids] of Object.entries(
    state.relationships.teacherToLearners,
  )) {
    if (lids.includes(learnerId)) teacherIds.add(tid)
  }

  return { enrollments, teacherIds }
}

/**
 * Return all parents linked to a learner, with display names resolved.
 * Excludes any parent IDs that no longer resolve to an account.
 */
function getLearnerParents(
  state: AccountsState,
  learnerId: string,
): Array<{ id: string; displayName: string }> {
  const ids = state.relationships.learnerToParents[learnerId] || []
  const results: Array<{ id: string; displayName: string }> = []
  for (const id of ids) {
    const account = state.accountsById[id]
    if (account) {
      results.push({ id, displayName: account.displayName })
    }
  }
  return results
}

// ── validateLink (replaces validateAddLearnerToClassroom) ────────────

/**
 * Validate a linkage decision. Returns an ordered list of warnings
 * (possibly empty). Pure — no state mutation, no I/O.
 *
 * Ordering: grade → enrollment → teacher → parent → self-link. UI consumers
 * may rely on this stable ordering.
 */
export function validateLink(
  state: AccountsState,
  context: LinkContext,
): LinkWarning[] {
  const warnings: LinkWarning[] = []
  const learner = state.accountsById[context.learnerId]
  if (!learner || !isLearnerAccount(learner)) return warnings

  // Branch on context.kind so each context can apply its rule subset.
  if (context.kind === 'teacher-classroom') {
    const classroom = state.classrooms[context.classroomId]
    if (!classroom) return warnings

    // 1. Grade mismatch / grade unknown — mutually exclusive.
    if (learner.grade && learner.grade !== classroom.groupLevel) {
      warnings.push({
        type: 'grade_mismatch',
        severity: 'warning',
        message: `${learner.displayName} zit in groep ${learner.grade}, maar deze klas is groep ${classroom.groupLevel}. De leerling blijft groep-${learner.grade}-stof oefenen.`,
        learnerGrade: learner.grade,
        classroomGroupLevel: classroom.groupLevel,
      })
    } else if (!learner.grade) {
      warnings.push({
        type: 'grade_unknown',
        severity: 'info',
        message: `${learner.displayName} heeft nog geen groep ingesteld. Bij koppelen wordt groep ${classroom.groupLevel} overgenomen.`,
        classroomGroupLevel: classroom.groupLevel,
      })
    }

    // 2. Multi-classroom — other classrooms (different teachers).
    const scan = scanLearnerEnrollments(state, context.learnerId)
    for (const enr of scan.enrollments) {
      if (enr.classroomId === context.classroomId) continue
      if (enr.teacherId === context.teacherId) continue
      warnings.push({
        type: 'multi_classroom',
        severity: 'warning',
        message: `${learner.displayName} zit al in "${enr.classroomName}" van ${enr.teacherName}. Wil je dat deze leerling bij beide leraren zichtbaar is?`,
        classroomName: enr.classroomName,
        classroomId: enr.classroomId,
        teacherName: enr.teacherName,
        teacherId: enr.teacherId,
      })
    }

    // 3. Multi-teacher — direct teacher↔learner links not already covered
    //    by a multi_classroom warning above.
    for (const [otherTeacherId, learnerIds] of Object.entries(
      state.relationships.teacherToLearners,
    )) {
      if (otherTeacherId === context.teacherId) continue
      if (!learnerIds.includes(context.learnerId)) continue
      const alreadyWarned = warnings.some(
        (w) => w.type === 'multi_classroom' && w.teacherId === otherTeacherId,
      )
      if (alreadyWarned) continue
      const otherTeacher = state.accountsById[otherTeacherId]
      warnings.push({
        type: 'multi_teacher',
        severity: 'warning',
        message: `${learner.displayName} is al gekoppeld aan ${otherTeacher?.displayName || 'een andere leraar'}.`,
        teacherName: otherTeacher?.displayName || 'Onbekend',
        teacherId: otherTeacherId,
      })
    }

    return warnings
  }

  if (context.kind === 'teacher-direct') {
    // 0. Self-link check
    const existingLearners =
      state.relationships.teacherToLearners[context.teacherId] || []
    if (existingLearners.includes(context.learnerId)) {
      warnings.push({
        type: 'already_linked_self',
        severity: 'blocking',
        message: `${learner.displayName} is al gekoppeld aan jouw account.`,
      })
      return warnings
    }

    // 2. Multi-classroom
    const scan = scanLearnerEnrollments(state, context.learnerId)
    for (const enr of scan.enrollments) {
      if (enr.teacherId === context.teacherId) continue
      warnings.push({
        type: 'multi_classroom',
        severity: 'warning',
        message: `${learner.displayName} zit al in "${enr.classroomName}" van ${enr.teacherName}. Wil je dat deze leerling bij beide leraren zichtbaar is?`,
        classroomName: enr.classroomName,
        classroomId: enr.classroomId,
        teacherName: enr.teacherName,
        teacherId: enr.teacherId,
      })
    }

    // 3. Multi-teacher
    for (const [otherTeacherId, learnerIds] of Object.entries(
      state.relationships.teacherToLearners,
    )) {
      if (otherTeacherId === context.teacherId) continue
      if (!learnerIds.includes(context.learnerId)) continue
      const alreadyWarned = warnings.some(
        (w) => w.type === 'multi_classroom' && w.teacherId === otherTeacherId,
      )
      if (alreadyWarned) continue
      const otherTeacher = state.accountsById[otherTeacherId]
      warnings.push({
        type: 'multi_teacher',
        severity: 'warning',
        message: `${learner.displayName} is al gekoppeld aan ${otherTeacher?.displayName || 'een andere leraar'}.`,
        teacherName: otherTeacher?.displayName || 'Onbekend',
        teacherId: otherTeacherId,
      })
    }

    return warnings
  }

  if (context.kind === 'parent-learner') {
    // 0. Self-link check — if the calling parent is already linked, the
    //    mutation is a no-op and the UI should not proceed.
    const existingParents =
      state.relationships.learnerToParents[context.learnerId] || []
    if (existingParents.includes(context.parentId)) {
      warnings.push({
        type: 'already_linked_self',
        severity: 'blocking',
        message: `${learner.displayName} is al gekoppeld aan jouw account.`,
      })
      return warnings // Stop here — further warnings are noise.
    }

    // 1. Multi-parent — other parents already linked.
    const otherParents = getLearnerParents(state, context.learnerId).filter(
      (p) => p.id !== context.parentId,
    )
    if (otherParents.length > 0) {
      const names = otherParents.map((p) => p.displayName).join(', ')
      warnings.push({
        type: 'multi_parent',
        severity: 'warning',
        message:
          otherParents.length === 1
            ? `${learner.displayName} is al gekoppeld aan ${names}. Bevestig dat jij ook toegang mag krijgen.`
            : `${learner.displayName} is al gekoppeld aan ${otherParents.length} andere ouders (${names}). Bevestig dat jij ook toegang mag krijgen.`,
        otherParents,
      })
    }

    return warnings
  }

  // Exhaustiveness guard — TypeScript will flag this if a new LinkContext
  // kind is added without a branch.
  const _exhaustive: never = context
  void _exhaustive
  return warnings
}

// ── getLearnerLinkageFlags (replaces getLearnerClassroomFlags) ───────

/**
 * Compute persistent linkage flags for a learner. Returns a stable shape
 * regardless of whether the caller passes classroom/parent context — fields
 * default to safe `false` / empty arrays when their context is missing.
 *
 * Used to drive chips on learner rows (teacher portal), linked-child cards
 * (parent portal), and any other "this learner has something to know about"
 * surface. For action-time validation use {@link validateLink}.
 */
export function getLearnerLinkageFlags(
  state: AccountsState,
  learnerId: string,
  options: LinkageFlagsOptions = {},
): LearnerLinkageFlags {
  const { classroom = null, parentId = null } = options
  const learner = state.accountsById[learnerId] as LearnerAccount | undefined

  const { enrollments, teacherIds } = scanLearnerEnrollments(state, learnerId)
  const parents = getLearnerParents(state, learnerId)
  const otherParents = parentId
    ? parents.filter((p) => p.id !== parentId)
    : parents

  const otherEnrollments = enrollments.filter(
    (e) => !classroom || e.classroomId !== classroom.id,
  )

  const gradeMismatch = Boolean(
    learner?.grade && classroom && learner.grade !== classroom.groupLevel,
  )
  const gradeUnknown = Boolean(learner && !learner.grade)

  return {
    gradeMismatch,
    gradeUnknown,
    multiClassroom: enrollments.length >= 2,
    multiTeacher: teacherIds.size >= 2,
    multiParent: parents.length >= 2,
    otherEnrollments: otherEnrollments.sort((a, b) =>
      a.classroomName.localeCompare(b.classroomName),
    ),
    otherParents,
  }
}

/**
 * Convenience predicate: does this learner currently warrant any visual
 * indicator on a row/chip? Pure boolean OR of the flag bits.
 */
export function hasAnyLinkageFlag(flags: LearnerLinkageFlags): boolean {
  return (
    flags.gradeMismatch ||
    flags.gradeUnknown ||
    flags.multiClassroom ||
    flags.multiTeacher ||
    flags.multiParent
  )
}

/**
 * True if any warning in the list would visually gate the confirm CTA.
 * `info`-severity warnings (currently only `grade_unknown`) do not gate;
 * `warning` and `blocking` do.
 */
export function hasGatingWarning(warnings: LinkWarning[]): boolean {
  return warnings.some((w) => w.severity !== 'info')
}

/**
 * True if any warning in the list is `blocking` — the UI must refuse to
 * proceed.
 */
export function hasBlockingWarning(warnings: LinkWarning[]): boolean {
  return warnings.some((w) => w.severity === 'blocking')
}
